import React, { useState, useEffect } from "react";
import toast, { Toaster } from "react-hot-toast";
import * as Select from "@radix-ui/react-select";
import { MapPinIcon, XMarkIcon, CloudArrowDownIcon, CpuChipIcon, ArrowDownTrayIcon, MagnifyingGlassIcon, BuildingOffice2Icon, SparklesIcon, LightBulbIcon } from "@heroicons/react/24/outline";
import { BoltIcon, RocketLaunchIcon, CheckCircleIcon, ChevronUpDownIcon } from "@heroicons/react/24/solid";
import { apiClient } from "../../lib/axios";
import { SEARCH_DECISION_MAKERS, SEARCH_TASK_BY_ID } from "../../config/apiConfig";
import { useAuthStore } from "../../store/useAuthStore";
import { DataTable, Column, PaginationMeta } from "../../components/ui/DataTable";
import { subscribeToUserChannel, ProgressEvent } from "../../lib/echo";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";

interface DecisionMaker {
  name: string;
  title: string;
  company_name: string;
  company_domain?: string;
  company_type?: string;
  company_address?: string;
}

interface SearchResponse {
  cached: boolean;
  data?: DecisionMaker[];
  task_id?: string;
}

interface TaskResponse {
  status: "idle" | "pending" | "retrieving" | "extracting" | "completed" | "failed";
  results?: DecisionMaker[];
  progress?: number;
}

type TaskStatus = "idle" | "pending" | "retrieving" | "extracting" | "completed" | "failed";

const DEPTH_OPTIONS = [
  { value: "1", label: "Normal Search", desc: "1 credit" },
  { value: "2", label: "Deep Search ×1", desc: "2 credits" },
  { value: "3", label: "Deep Search ×2", desc: "3 credits" },
  { value: "4", label: "Deep Search ×3", desc: "4 credits" },
  { value: "5", label: "Deep Search ×4", desc: "5 credits" },
  { value: "6", label: "Deep Search ×5", desc: "6 credits" },
];

const DEFAULT_META: PaginationMeta = { total: 0, page: 1, per_page: 20, last_page: 1 };

const LEAD_COLUMNS: Column<DecisionMaker>[] = [
  {
    key: "name",
    header: "Name",
    width: "180px",
    render: (val) => <span className="font-semibold text-slate-900">{val || "—"}</span>,
  },
  { key: "title", header: "Title", width: "160px", render: (val) => val || "—" },
  {
    key: "company_domain",
    header: "Domain",
    width: "150px",
    render: (val) => {
      const d = val && val.toLowerCase() !== "unknown" ? val.trim() : "";
      return d ? (
        <a href={`https://${d}`} target="_blank" rel="noreferrer" className="text-brand-600 hover:underline text-xs font-semibold">
          {d}
        </a>
      ) : (
        <span className="text-slate-500">—</span>
      );
    },
  },
];

const PIPELINE_STEPS = [
  { key: "pending", label: "Query Generation", icon: MagnifyingGlassIcon, color: "brand" },
  { key: "retrieving", label: "Smart Retrieval", icon: CloudArrowDownIcon, color: "blue" },
  { key: "extracting", label: "AI Extraction", icon: CpuChipIcon, color: "violet" },
];

function stepState(step: string, status: TaskStatus): "done" | "active" | "waiting" {
  const order = ["pending", "retrieving", "extracting", "completed"];
  const stepIdx = order.indexOf(step);
  const statusIdx = order.indexOf(status);
  if (status === "failed") return "waiting";
  if (statusIdx > stepIdx) return "done";
  if (statusIdx === stepIdx) return "active";
  return "waiting";
}

const colorMap: Record<string, { bg: string; bar: string; text: string }> = {
  brand: { bg: "bg-brand-50", bar: "bg-brand-500", text: "text-slate-800" },
  blue: { bg: "bg-blue-50", bar: "bg-blue-500", text: "text-blue-900" },
  violet: { bg: "bg-violet-50", bar: "bg-violet-500", text: "text-violet-900" },
};

export default function Search() {
  useDocumentTitle("Decision Maker Search", "Find founders, executives, and decision makers at any company using AI-powered extraction.");
  const { user } = useAuthStore();
  const [company, setCompany] = useState("");
  const [location, setLocation] = useState("");
  const [titles, setTitles] = useState<string[]>([]);
  const [titleInput, setTitleInput] = useState("");
  const [searchDepth, setSearchDepth] = useState(1);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [taskStatus, setTaskStatus] = useState<TaskStatus>("idle");
  const [taskProgress, setTaskProgress] = useState(0);

  const [allResults, setAllResults] = useState<DecisionMaker[]>([]);
  const [pagedResults, setPagedResults] = useState<DecisionMaker[]>([]);
  const [resultsMeta, setResultsMeta] = useState<PaginationMeta>(DEFAULT_META);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [taskIdForResults, setTaskIdForResults] = useState<string | null>(null);

  const isBusy = taskStatus === "pending" || taskStatus === "retrieving" || taskStatus === "extracting";

  const handleAddTitle = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && titleInput.trim() && titles.length < 5) {
      setTitles((prev) => [...prev, titleInput.trim()]);
      setTitleInput("");
    }
  };

  const reset = () => {
    setTaskId(null);
    setTaskStatus("idle");
    setTaskProgress(0);
    setAllResults([]);
    setPagedResults([]);
    setResultsMeta(DEFAULT_META);
    setTaskIdForResults(null);
  };

  const fetchPagedResults = async (tid: string, page: number) => {
    setResultsLoading(true);
    try {
      const res = await apiClient.get(`/search/tasks/${tid}/results`, { params: { page, per_page: 20 } });
      setPagedResults(res.data.data);
      setResultsMeta(res.data.meta);
    } catch {
      toast.error("Failed to load results page.");
    } finally {
      setResultsLoading(false);
    }
  };

  const startSearch = async () => {
    if (!company || titles.length === 0) {
      toast.error("Company and at least one title are required.");
      return;
    }
    if (user && (user.credits ?? 0) < searchDepth) {
      toast.error(`Insufficient credits. Need ${searchDepth}, have ${user.credits ?? 0}.`);
      return;
    }
    reset();
    setTaskStatus("pending");

    try {
      const res = await apiClient.post<SearchResponse>(SEARCH_DECISION_MAKERS, {
        company,
        location,
        titles,
        depth: searchDepth,
      });

      if (res.data.cached && res.data.data) {
        const data = res.data.data;
        setAllResults(data);
        setResultsMeta({ total: data.length, page: 1, per_page: 20, last_page: Math.max(1, Math.ceil(data.length / 20)) });
        setPagedResults(data.slice(0, 20));
        setTaskStatus("completed");
        toast.success(`Found ${data.length} decision makers (cached).`);
      } else if (res.data.task_id) {
        setTaskId(res.data.task_id);
        toast.success("Search started. Gathering intelligence…");
      }
    } catch {
      setTaskStatus("failed");
      toast.error("Failed to start search.");
    }
  };

  const handleCachedPageChange = (page: number) => {
    const start = (page - 1) * 20;
    setPagedResults(allResults.slice(start, start + 20));
    setResultsMeta((prev) => ({ ...prev, page }));
  };

  const handleExportCSV = async () => {
    try {
      const res = await apiClient.get(`/search/download/${taskId || taskIdForResults}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", "results.csv");
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Download started!");
    } catch {
      toast.error("Download failed.");
    }
  };

  useEffect(() => {
    if (!taskId || taskStatus === "completed" || taskStatus === "failed") return;

    const unsubscribePusher = user?.id
      ? subscribeToUserChannel(user.id, (e: ProgressEvent) => {
          if (e.task_id !== taskId) return;
          const s = e.status as TaskStatus;
          setTaskStatus(s);
          if (e.progress !== undefined) setTaskProgress(e.progress);
          if (s === "completed") {
            setTaskIdForResults(taskId);
            fetchPagedResults(taskId, 1);
            toast.success("Search complete!");
          } else if (s === "failed") {
            toast.error("Search failed.");
          }
        })
      : () => {};

    const interval = setInterval(async () => {
      try {
        const res = await apiClient.get<TaskResponse>(`${SEARCH_TASK_BY_ID}${taskId}`);
        setTaskStatus(res.data.status);
        if (res.data.progress !== undefined) setTaskProgress(res.data.progress);
        if (res.data.status === "completed") {
          clearInterval(interval);
          setTaskIdForResults(taskId);
          fetchPagedResults(taskId, 1);
          toast.success("Search complete!");
        } else if (res.data.status === "failed") {
          clearInterval(interval);
          toast.error("Search failed.");
        }
      } catch {}
    }, 3000);

    return () => {
      unsubscribePusher();
      clearInterval(interval);
    };
  }, [taskId, taskStatus, user?.id]);

  const showResults = taskStatus === "completed";
  const canSearch = taskStatus === "idle" || taskStatus === "completed" || taskStatus === "failed";

  return (
    <div className="h-full flex flex-col bg-slate-50/50">
      <Toaster position="top-center" />
      <style>{`
        @keyframes slide{0%{transform:translateX(-100%)}100%{transform:translateX(200%)}}
        .animate-slide{animation:slide 1.5s infinite linear}
        @keyframes selectIn{from{opacity:0;transform:translateY(-6px) scale(0.97)}to{opacity:1;transform:translateY(0) scale(1)}}
        @keyframes selectOut{from{opacity:1;transform:translateY(0) scale(1)}to{opacity:0;transform:translateY(-4px) scale(0.98)}}
        [data-radix-select-content][data-state='open']{animation:selectIn 160ms cubic-bezier(0.16,1,0.3,1)}
        [data-radix-select-content][data-state='closed']{animation:selectOut 100ms ease-in}
        @keyframes fadeIn{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:translateY(0)}}
        .fade-in{animation:fadeIn 0.22s ease-out both}
      `}</style>

      <div className="flex-1 px-6 md:px-10 py-8 overflow-y-auto">
        <div className="max-w-[1200px] mx-auto flex flex-col gap-8">
          {/* Page heading */}
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">Decision Maker Search</h1>
            <p className="text-sm text-slate-500 mt-1">Find founders, executives, and decision makers at any company.</p>
          </div>

          {/* Search form */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-6">
              <div className="md:col-span-4">
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-2">Company *</label>
                <input type="text" value={company} onChange={(e) => setCompany(e.target.value)} onKeyDown={(e) => e.key === "Enter" && canSearch && startSearch()} placeholder="e.g. Lucky Restaurant, Cafe Noir" className="w-full text-base font-semibold text-slate-900 placeholder:text-slate-500 bg-transparent border-none p-0 focus:ring-0 outline-none" />
              </div>

              <div className="hidden md:block w-px bg-slate-100 self-stretch" />

              <div className="md:col-span-6">
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-2">Address *</label>
                <div className="flex items-center gap-2">
                  <MapPinIcon className="w-4 h-4 text-slate-500 shrink-0" />
                  <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. 123 Main St, San Francisco, California, 94102" className="w-full text-base font-semibold text-slate-900 placeholder:text-slate-500 bg-transparent border-none p-0 focus:ring-0 outline-none" />
                </div>
              </div>

              <div className="hidden md:block w-px bg-slate-100 self-stretch" />

              <div className="md:col-span-3">
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-2">
                  Roles ({titles.length}/5) {titles.length >= 5 && <span className="text-red-500 ml-1">Max</span>}
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  {titles.map((t, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-brand-200 text-slate-800 border border-brand-400 px-2.5 py-1 rounded-full text-sm font-semibold">
                      {t}
                      <button onClick={() => setTitles(titles.filter((_, idx) => idx !== i))}>
                        <XMarkIcon className="w-3.5 h-3.5 text-slate-500 hover:text-red-500" />
                      </button>
                    </span>
                  ))}
                  {titles.length < 5 && <input type="text" placeholder="+ Add role" value={titleInput} onChange={(e) => setTitleInput(e.target.value)} onKeyDown={handleAddTitle} className="bg-transparent border-none p-0 text-sm font-semibold text-brand-900 placeholder:text-slate-500 focus:ring-0 outline-none w-20" />}
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between pt-5 border-t border-slate-100 gap-4">
              {/* Depth selector + info badge */}
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wide shrink-0">Search Depth:</span>
                  <Select.Root value={searchDepth.toString()} onValueChange={(v) => setSearchDepth(parseInt(v))}>
                    <Select.Trigger className="inline-flex items-center justify-between min-w-[200px] bg-white border border-slate-200 px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-brand-500/20 shadow-sm hover:border-slate-300 transition-all">
                      <Select.Value />
                      <Select.Icon>
                        <ChevronUpDownIcon className="w-4 h-4 text-slate-400" />
                      </Select.Icon>
                    </Select.Trigger>
                    <Select.Portal>
                      <Select.Content position="popper" sideOffset={6} data-radix-select-content="" className="z-50 bg-white border border-slate-200 shadow-xl rounded-xl overflow-hidden min-w-[220px]">
                        <Select.Viewport className="p-2">
                          {DEPTH_OPTIONS.map((opt) => (
                            <Select.Item key={opt.value} value={opt.value} className="flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-700 cursor-pointer outline-none hover:bg-brand-50 hover:text-brand-800 data-[state=checked]:bg-brand-500 data-[state=checked]:text-slate-900 transition-colors">
                              <Select.ItemText>{opt.label}</Select.ItemText>
                              <span className="text-[11px] font-medium opacity-60 ml-3">{opt.desc}</span>
                            </Select.Item>
                          ))}
                        </Select.Viewport>
                      </Select.Content>
                    </Select.Portal>
                  </Select.Root>
                </div>
                <span className="text-[10px] font-bold text-slate-900 uppercase tracking-wider px-3 py-1 bg-brand-500 rounded-lg whitespace-nowrap">Per Company • Live Search</span>
              </div>

              <button onClick={startSearch} disabled={!canSearch} className="w-full sm:w-auto bg-brand-500 text-slate-900 px-8 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-brand-600 transition-colors [box-shadow:0_4px_16px_rgba(0,84,214,0.35)] hover:[box-shadow:0_6px_20px_rgba(0,84,214,0.45)] disabled:opacity-50 disabled:shadow-none">
                {isBusy ? (
                  <>
                    <RocketLaunchIcon className="w-4 h-4 animate-pulse" /> Searching…
                  </>
                ) : (
                  <>
                    <BoltIcon className="w-4 h-4" /> Find Decision Makers
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Hints — visible only when idle */}
          {taskStatus === "idle" && (
            <div className="fade-in flex flex-col gap-6">
              {/* Depth explainer */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <SparklesIcon className="w-4 h-4 text-slate-400" />
                  <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">How Search Depth Works</span>
                </div>
                <div className="rounded-2xl shadow-sm overflow-hidden border border-slate-100">
                  <div className="grid grid-cols-1 sm:grid-cols-6 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
                    {[
                      { depth: 1, label: "Normal", sub: "1 AI pass", credits: "1 credit", color: "text-slate-600" },
                      { depth: 2, label: "Deep ×1", sub: "2 AI passes", credits: "2 credits", color: "text-brand-700" },
                      { depth: 3, label: "Deep ×2", sub: "3 AI passes", credits: "3 credits", color: "text-brand-700" },
                      { depth: 4, label: "Deep ×3", sub: "4 AI passes", credits: "4 credits", color: "text-brand-700" },
                      { depth: 5, label: "Deep ×4", sub: "5 AI passes", credits: "5 credits", color: "text-brand-700" },
                      { depth: 6, label: "Deep ×5", sub: "6 AI passes", credits: "6 credits", color: "text-brand-700" },
                    ].map((d, i) => (
                      <button key={d.depth} onClick={() => setSearchDepth(d.depth)} className={`p-4 text-left transition-colors ${searchDepth === d.depth ? "bg-brand-600" : "bg-white hover:bg-slate-50"}`}>
                        <p className={`text-[10px] font-bold uppercase tracking-wide mb-2 ${searchDepth === d.depth ? "text-slate-900" : "text-slate-400"}`}>{i === 0 ? "Standard" : `Option ${i + 1}`}</p>
                        <p className={`text-sm font-extrabold leading-none mb-1 ${searchDepth === d.depth ? "text-slate-900" : d.color}`}>{d.label}</p>
                        <p className={`text-[11px] font-medium mb-2 ${searchDepth === d.depth ? "text-slate-900" : "text-slate-500"}`}>{d.sub}</p>
                        <p className={`text-[10px] font-bold uppercase tracking-wider ${searchDepth === d.depth ? "text-slate-900" : "text-slate-400"}`}>{d.credits}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quick tips */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <LightBulbIcon className="w-4 h-4 text-slate-400" />
                  <span className="text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">Quick Tips</span>
                </div>
                <div className="rounded-2xl shadow-sm overflow-hidden border border-slate-100">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-slate-100">
                    {[
                      { n: "01", title: "Use the company's exact brand name", body: '"Lucky\'s Restaurant" works better than "Lucky\'s".' },
                      { n: "02", title: "Add 3–5 job titles for best coverage", body: 'Decision makers hold many titles. Combine "Owner", "Manager", "CEO" in one search. Maximum 5 titles per search.' },
                      { n: "03", title: "Full address pinpoints the right business", body: 'Many businesses share the same name across locations. Include city, state, and zipcode - e.g. "123 Main St, San Francisco, California, 94102" - to find the exact location. We charge per company, not per person.' },
                    ].map((tip, i) => (
                      <div key={i} className="p-5 bg-white">
                        <p className="text-[9px] font-bold text-slate-700 uppercase tracking-wider mb-2">Tip {tip.n}</p>
                        <p className="text-[13px] font-bold text-slate-800 mb-1.5 leading-snug">{tip.title}</p>
                        <p className="text-[12px] text-slate-500 leading-relaxed">{tip.body}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pipeline + results */}
          {taskStatus !== "idle" && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Pipeline */}
              <div className="lg:col-span-3 flex flex-col gap-3">
                <h3 className="text-[11px] font-extrabold text-slate-400 uppercase tracking-wide">Live Pipeline</h3>
                <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-5">
                  {PIPELINE_STEPS.map((step) => {
                    const state = stepState(step.key, taskStatus);
                    const c = colorMap[step.color];
                    const Icon = step.icon;
                    return (
                      <div key={step.key} className={`flex items-start gap-4 transition-opacity ${state === "waiting" ? "opacity-30" : "opacity-100"}`}>
                        <div className={`w-9 h-9 rounded-xl ${c.bg} flex items-center justify-center shrink-0 mt-0.5`}>{state === "done" ? <CheckCircleIcon className={`w-4 h-4 ${c.text}`} /> : <Icon className={`w-4 h-4 ${c.text}`} />}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[12px] font-bold text-slate-700">{step.label}</span>
                            <span className={`text-[10px] font-bold uppercase ${state === "active" ? c.text : "text-slate-400"}`}>{state === "done" ? "Done" : state === "active" ? "Active" : "Wait"}</span>
                          </div>
                          <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${state === "done" ? `${c.bar} w-full` : state === "active" ? `${c.bar} w-1/2 animate-slide` : "w-0"}`} />
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {isBusy && (
                    <div className="pt-2 border-t border-slate-50">
                      <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full w-1/3 bg-brand-500 rounded-full animate-slide" />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Results */}
              <div className="lg:col-span-9 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-[11px] font-extrabold text-slate-400 uppercase tracking-wide">Results {showResults && `· ${resultsMeta.total.toLocaleString()} leads`}</h3>
                  {showResults && (taskId || taskIdForResults) && (
                    <button onClick={handleExportCSV} className="flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-xl text-xs font-bold hover:bg-slate-50 transition-colors shadow-sm">
                      <ArrowDownTrayIcon className="w-3.5 h-3.5" /> Export CSV
                    </button>
                  )}
                </div>

                {taskStatus === "failed" ? <div className="bg-white border border-red-100 rounded-2xl p-8 text-center text-red-400 text-sm font-medium shadow-sm">Search failed. Please try again.</div> : <DataTable<DecisionMaker> columns={LEAD_COLUMNS} data={pagedResults} meta={resultsMeta} onPageChange={taskIdForResults ? (page) => fetchPagedResults(taskIdForResults, page) : handleCachedPageChange} isLoading={resultsLoading || isBusy} emptyMessage={isBusy ? "Gathering intelligence…" : "No decision makers found."} />}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
